import { useSceneActivity } from "./ui";
import { useSiteReducedMotion, useForwardProgress } from "./motion-preferences";
import { useId, useState, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const layers = [
  {
    label: "Applications",
    title: "Applications for your business",
    detail: "Web · Mobile · Client portals",
    y: 119,
  },
  {
    label: "Architecture",
    title: "Designed around your needs",
    detail: "Platforms · Integrations · Data flows",
    y: 207,
  },
  {
    label: "Security",
    title: "Security & maintainability",
    detail: "Considered from the start",
    y: 295,
  },
];
const traces = [
  "M-102-66H-58V-20H-31",
  "M-104-15H-78V13H-31",
  "M-99 62H-53V24H-31",
  "M102-70H55V-17H31",
  "M104 10H77V6H31",
  "M101 68H49V21H31",
  "M-20-103V-63H-9V-31",
  "M66-99V-67H17V-31",
  "M-64 104V57H-19V31",
  "M19 104V31",
];
function PlaneContent({ index }) {
  if (index === 0)
    return (
      <g className="experience-surface">
        <rect
          x="-99"
          y="-98"
          width="198"
          height="22"
          rx="5"
          fill="#fddf47"
          fillOpacity=".15"
        />
        <circle cx="-85" cy="-87" r="2" />
        <circle cx="-75" cy="-87" r="2" opacity=".55" />
        <circle cx="-65" cy="-87" r="2" opacity=".3" />
        <path d="M61-87H87" stroke="currentColor" strokeOpacity=".7" />
        <rect
          x="-99"
          y="-64"
          width="55"
          height="154"
          rx="5"
          fill="#ddcd8f"
          fillOpacity=".07"
          stroke="currentColor"
          strokeOpacity=".25"
        />
        <path
          d="M-85-46H-59M-85-26H-65M-85-6H-59M-85 14H-70"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          opacity=".45"
        />
        <rect
          x="-32"
          y="-64"
          width="131"
          height="69"
          rx="5"
          fill="#fddf47"
          fillOpacity=".13"
          stroke="currentColor"
          strokeOpacity=".45"
        />
        <path
          d="M-19-8-3-24 17-18 34-42 53-32 75-49 88-55"
          stroke="currentColor"
          strokeWidth="2"
          fill="none"
        />
        <rect
          x="-32"
          y="18"
          width="59"
          height="72"
          rx="5"
          stroke="currentColor"
          strokeOpacity=".35"
        />
        <rect
          x="40"
          y="18"
          width="59"
          height="72"
          rx="5"
          stroke="currentColor"
          strokeOpacity=".35"
        />
        <path
          d="M-19 38H13M-19 49H5M-19 72H13M53 38H85M53 49H74M53 72H85"
          stroke="currentColor"
          strokeWidth="3"
          opacity=".5"
        />
      </g>
    );
  if (index === 1)
    return (
      <g className="architecture-surface">
        {traces.map((d, i) => (
          <g key={d}>
            <path
              d={d}
              stroke="currentColor"
              strokeWidth="1.1"
              strokeOpacity=".5"
            />
            <path
              className="circuit-trace"
              d={d}
              pathLength="1"
              stroke="currentColor"
              strokeWidth="2"
              style={{ animationDelay: `${i * 0.16}s` }}
            />
          </g>
        ))}
        <rect
          x="-37"
          y="-37"
          width="74"
          height="74"
          rx="9"
          stroke="currentColor"
          strokeOpacity=".4"
        />
        <rect
          x="-29"
          y="-29"
          width="58"
          height="58"
          rx="6"
          fill="#fddf47"
          fillOpacity=".17"
          stroke="currentColor"
        />
        <path
          d="M-11-10-21 0-11 10M11-10 21 0 11 10M4-15-4 15"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {[-86, -62, 62, 86].map((x) => (
          <g key={x}>
            <circle cx={x} cy="-102" r="2.2" fill="currentColor" />
            <circle cx={x} cy="102" r="2.2" fill="currentColor" />
          </g>
        ))}
      </g>
    );
  return (
    <g className="security-surface">
      {[-75, -50, -25, 0, 25, 50, 75].map((n) => (
        <g key={n}>
          <path
            d={`M${n}-97V97M-97 ${n}H97`}
            stroke="currentColor"
            strokeOpacity=".13"
          />
        </g>
      ))}
      <rect
        x="-93"
        y="-93"
        width="186"
        height="186"
        rx="14"
        stroke="currentColor"
        strokeOpacity=".55"
        strokeDasharray="3 5"
      />
      <rect
        x="-67"
        y="-67"
        width="134"
        height="134"
        rx="25"
        stroke="currentColor"
        strokeOpacity=".18"
      />
      <path
        d="M0-34 27-23V-2C27 15 12 29 0 35-12 29-27 15-27-2V-23Z"
        fill="#fddf47"
        fillOpacity=".08"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="m-12 0 9 9 16-19"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {[-93, 93].flatMap((x) =>
        [-93, 93].map((y) => (
          <rect
            key={`${x}:${y}`}
            x={x - 5}
            y={y - 5}
            width="10"
            height="10"
            rx="2"
            fill="#fddf47"
            fillOpacity=".6"
          />
        )),
      )}
    </g>
  );
}
function ScrollPlane({ index, progress, children }) {
  const reduced = useSiteReducedMotion();
  const y = useTransform(progress, [0, 1], [(1 - index) * 64, 0]);
  return <motion.g style={{ y: reduced ? 0 : y }}>{children}</motion.g>;
}
export function EngineeringSystem() {
  const sceneRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sceneRef,
    offset: ["start 95%", "center 55%"],
  });
  const assembly = useForwardProgress(scrollYProgress);
  const { ref: activityRef, active: running } = useSceneActivity();
  const [active, setActive] = useState(1);
  const reduced = useSiteReducedMotion();
  const id = useId().replace(/:/g, "");
  return (
    <div className="engineering-system" ref={sceneRef}>
      <div className="engineering-system-header">
        <span>
          <i />
          BUILT FOR THE LONG TERM
        </span>
        <span>QC / 04</span>
      </div>
      <div
        className="engineering-scene"
        ref={activityRef}
        data-running={running}
        aria-hidden="true"
      >
        <svg viewBox="0 0 520 415" fill="none">
          <defs>
            <radialGradient id={`${id}-ambient`}>
              <stop stopColor="#fddf47" stopOpacity=".12" />
              <stop offset="1" stopColor="#fddf47" stopOpacity="0" />
            </radialGradient>
            <linearGradient id={`${id}-plane`} x1="0" y1="0" x2="1" y2="1">
              <stop stopColor="#fff1a3" stopOpacity=".15" />
              <stop offset="1" stopColor="#7c6a2b" stopOpacity=".04" />
            </linearGradient>
            <linearGradient id={`${id}-edge`}>
              <stop stopColor="#cab965" stopOpacity=".26" />
              <stop offset="1" stopColor="#7c6a2b" stopOpacity=".03" />
            </linearGradient>
          </defs>
          <ellipse
            cx="260"
            cy="220"
            rx="248"
            ry="180"
            fill={`url(#${id}-ambient)`}
          />
          <path
            d="M72 282 260 376 448 282M72 302 260 396 448 302"
            stroke="#a49971"
            strokeOpacity=".09"
          />
          <path
            d="M82 119V300M438 119V300M260 31V209"
            stroke="#c8bd8d"
            strokeDasharray="2 6"
            strokeOpacity=".23"
          />
          {[2, 1, 0].map((index) => {
            const layer = layers[index];
            return (
              <ScrollPlane key={index} index={index} progress={assembly}>
                <g
                  className="engineering-drift"
                  style={{ animationDelay: `${index * 0.45}s` }}
                >
                  <motion.g
                    initial={false}
                    animate={{
                      y: active === index ? -9 : 0,
                      opacity: active === index ? 1 : 0.48,
                    }}
                    transition={{
                      duration: reduced ? 0 : 0.65,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className={`engineering-plane ${active === index ? "is-selected" : ""}`}
                  >
                    <path
                      d={`M84 ${layer.y} 260 ${layer.y + 88} 436 ${layer.y}V${layer.y + 7}L260 ${layer.y + 95} 84 ${layer.y + 7}Z`}
                      fill={`url(#${id}-edge)`}
                      stroke="#b9a86b"
                      strokeOpacity=".18"
                    />
                    <g
                      transform={`translate(260 ${layer.y}) matrix(.8 .4 -.8 .4 0 0)`}
                    >
                      <rect
                        x="-110"
                        y="-110"
                        width="220"
                        height="220"
                        rx="10"
                        fill="#191918"
                        fillOpacity=".7"
                      />
                      <rect
                        x="-110"
                        y="-110"
                        width="220"
                        height="220"
                        rx="10"
                        fill={`url(#${id}-plane)`}
                        stroke="#fddf47"
                        strokeWidth={active === index ? 1.8 : 1}
                        strokeOpacity={active === index ? 0.85 : 0.38}
                      />
                      <PlaneContent index={index} />
                    </g>
                  </motion.g>
                </g>
              </ScrollPlane>
            );
          })}
          <g fill="#bfb489" fontSize="10" fontFamily="inherit" opacity=".55">
            <text x="55" y="118">
              01
            </text>
            <text x="55" y="206">
              02
            </text>
            <text x="55" y="294">
              03
            </text>
          </g>
        </svg>
      </div>
      <div
        className="engineering-layer-controls"
        role="group"
        aria-label="Explore engineering layers"
      >
        {layers.map((layer, i) => (
          <button
            key={layer.label}
            onClick={() => setActive(i)}
            aria-pressed={active === i}
            aria-controls={`${id}-detail`}
          >
            <span>0{i + 1}</span>
            {layer.label}
          </button>
        ))}
      </div>
      <div
        className="engineering-layer-detail"
        id={`${id}-detail`}
        aria-live="polite"
      >
        <div>
          <h3>{layers[active].title}</h3>
          <p>{layers[active].detail}</p>
        </div>
        <ArrowUpRight size={23} strokeWidth={1.2} aria-hidden="true" />
      </div>
      <div className="engineering-system-footer">
        Precision. Speed. Elegance.
      </div>
    </div>
  );
}
